import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, Wallet, TrendingUp, Mic, ArrowRight, Check } from "lucide-react";
import { PageHero, SectionHeader, EmeraldButton } from "@/components/Bits";
import { LIFESTYLE } from "@/lib/brand";

const services = [
  {
    icon: Sparkles,
    title: "Digital Product Strategy",
    body: "Transform expertise into elegant digital products — courses, blueprints, libraries, and templates that monetize what you already know.",
    bullets: ["Product positioning", "Pricing architecture", "Launch sequencing"],
  },
  {
    icon: Clock,
    title: "Productivity & Time Freedom Coaching",
    body: "A bespoke coaching engagement to reduce overwhelm, restore focus, and engineer a week that honors your life.",
    bullets: ["Energy & priorities audit", "Calendar architecture", "Boundary scripts"],
  },
  {
    icon: Wallet,
    title: "Financial Wellness Education",
    body: "Evidence-based education to strengthen money confidence, support stronger decision-making, and build economic resilience.",
    bullets: ["Financial literacy foundations", "Confidence-building practices", "Personalized roadmap"],
  },
  {
    icon: TrendingUp,
    title: "Business Visibility & Growth",
    body: "Refined brand presence, content systems, and audience strategy for women entrepreneurs serious about growth.",
    bullets: ["Brand positioning", "Content strategy", "Visibility planning"],
  },
  {
    icon: Mic,
    title: "Speaking & Workshops",
    body: "Keynotes, breakouts, and workshops on digital wellness, executive productivity, and economic empowerment.",
    bullets: ["Keynote speaking", "Corporate workshops", "Community programming"],
  },
];

const Services = () => (
  <div data-testid="page-services">
    <PageHero
      overline="Executive Services"
      title="Bespoke programs for women redesigning the way they work."
      subtitle="Five disciplines, designed to be combined. Each engagement is private, strategic, and shaped to your standards."
      image={LIFESTYLE.meeting}
    />

    <section className="ess-section bg-ess-paper">
      <div className="ess-container space-y-px bg-ess-blush border border-ess-blush">
        {services.map(({ icon: Icon, title, body, bullets }, i) => (
          <div
            key={title}
            data-testid={`services-${title.toLowerCase().replace(/\W+/g, "-")}`}
            className="bg-white p-10 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start group hover:bg-ess-paper transition-colors"
          >
            <div className="lg:col-span-1">
              <p className="font-display text-4xl text-ess-gold">0{i + 1}</p>
            </div>
            <div className="lg:col-span-3 flex lg:flex-col items-center lg:items-start gap-4">
              <div className="w-14 h-14 border border-ess-emerald/15 flex items-center justify-center text-ess-emerald group-hover:bg-ess-emerald group-hover:text-ess-gold transition-all">
                <Icon size={22} strokeWidth={1.4} />
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-ess-emerald leading-tight">
                {title}
              </h3>
            </div>
            <div className="lg:col-span-5">
              <p className="text-ess-slate leading-relaxed">{body}</p>
              <ul className="mt-5 space-y-2 text-sm text-ess-charcoal">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check size={14} className="text-ess-gold-deep mt-1" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <EmeraldButton as="link" to="/book">
                Inquire <ArrowRight size={14} className="ml-3" />
              </EmeraldButton>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="ess-section bg-ess-blush">
      <div className="ess-container text-center max-w-2xl mx-auto">
        <p className="ess-overline">Not sure where to begin?</p>
        <h2 className="font-display text-4xl md:text-5xl text-ess-emerald mt-4">
          A 30-minute consultation will reveal the path.
        </h2>
        <div className="mt-8 flex justify-center">
          <EmeraldButton as="link" to="/book" data-testid="services-cta-book">
            Book a Consultation <ArrowRight size={14} className="ml-3" />
          </EmeraldButton>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
